import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Logger from "../../services/Logger";
import { Box, Button, Card, CardContent, CircularProgress, Divider, Grid, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import UtilService from "../../services/UtilService";
import { ADMIN_CRM_REFERRAL_PATH } from "../crm/crmRoutes";
import ReferralService from "./ReferralService";

export default function ReferralEventDetail() {
    const { eventId } = useParams();
    const navigate = useNavigate();
    const [event, setEvent] = useState(null);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (eventId) {
            setLoading(true);
            setError(null);
            setEvent(null);
            setStats(null);

            Promise.all([
                ReferralService.getEvent(eventId),
                ReferralService.getStats(eventId),
            ])
                .then(([eventData, statsData]) => {
                    setEvent(eventData);
                    setStats(statsData);
                    setLoading(false);
                })
                .catch((err) => {
                    Logger.error("Error fetching event details:", err);
                    setError(err.message || "Failed to load event details");
                    setLoading(false);
                });
        }
    }, [eventId]);

    const handleBackClick = () => {
        navigate(ADMIN_CRM_REFERRAL_PATH);
    };

    return (
        <Box sx={{ flexGrow: 1, gap: 2, display: "flex", flexDirection: "column" }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBackClick}
                    sx={{ mr: 2 }}
                >
                    Back
                </Button>
                <Typography variant="h5">Referral Event Details</Typography>
            </Box>

            {loading && (
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {error && (
                <Box sx={{ p: 2 }}>
                    <Typography color="error">{error}</Typography>
                </Box>
            )}

            {!loading && !error && event && (
                <Grid container spacing={2}>
                    {/* Event Information */}
                    <Grid size={{ xs: 12 }}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Event Information
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Name
                                        </Typography>
                                        <Typography variant="body1">{event.name || "-"}</Typography>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Required Count
                                        </Typography>
                                        <Typography variant="body1">{event.requiredCount || "-"}</Typography>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Reward Months
                                        </Typography>
                                        <Typography variant="body1">{event.rewardMonths || "-"}</Typography>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Start Date
                                        </Typography>
                                        <Typography variant="body1">
                                            {event.startAt ? UtilService.formatDate(event.startAt) : "-"}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            End Date
                                        </Typography>
                                        <Typography variant="body1">
                                            {event.endAt ? UtilService.formatDate(event.endAt) : "-"}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Created At
                                        </Typography>
                                        <Typography variant="body1">
                                            {event.createdAt ? UtilService.formatDate(event.createdAt) : "-"}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Event Statistics */}
                    {stats && stats.eventStats && (
                        <Grid size={{ xs: 12 }}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Event Statistics
                                    </Typography>
                                    <Divider sx={{ mb: 2 }} />
                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Inviters
                                            </Typography>
                                            <Typography variant="h5" sx={{ fontWeight: "medium" }}>
                                                {stats.eventStats.inviters || 0}
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Invites
                                            </Typography>
                                            <Typography variant="h5" sx={{ fontWeight: "medium" }}>
                                                {stats.eventStats.invites || 0}
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Reward Granted
                                            </Typography>
                                            <Typography variant="h5" sx={{ fontWeight: "medium" }}>
                                                {stats.eventStats.rewardGranted || 0}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                    {stats.eventStats.inviteStatusCounts && (
                                        <>
                                            <Divider sx={{ my: 2 }} />
                                            <Typography variant="subtitle1" gutterBottom>
                                                Invite Status Counts
                                            </Typography>
                                            <Grid container spacing={2} sx={{ mt: 1 }}>
                                                <Grid size={{ xs: 6, sm: 3 }}>
                                                    <Typography variant="subtitle2" color="text.secondary">
                                                        Sent
                                                    </Typography>
                                                    <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                                                        {stats.eventStats.inviteStatusCounts.sent || 0}
                                                    </Typography>
                                                </Grid>
                                                <Grid size={{ xs: 6, sm: 3 }}>
                                                    <Typography variant="subtitle2" color="text.secondary">
                                                        Active
                                                    </Typography>
                                                    <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                                                        {stats.eventStats.inviteStatusCounts.joined || 0}
                                                    </Typography>
                                                </Grid>
                                                <Grid size={{ xs: 6, sm: 3 }}>
                                                    <Typography variant="subtitle2" color="text.secondary">
                                                        Verified
                                                    </Typography>
                                                    <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                                                        {stats.eventStats.inviteStatusCounts.verified || 0}
                                                    </Typography>
                                                </Grid>
                                                <Grid size={{ xs: 6, sm: 3 }}>
                                                    <Typography variant="subtitle2" color="text.secondary">
                                                        Rejected
                                                    </Typography>
                                                    <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                                                        {stats.eventStats.inviteStatusCounts.rejected || 0}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    )}

                    {/* Overall Statistics */}
                    {stats && (
                        <Grid size={{ xs: 12 }}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Overall Statistics
                                    </Typography>
                                    <Divider sx={{ mb: 2 }} />
                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Total Events
                                            </Typography>
                                            <Typography variant="h5" sx={{ fontWeight: "medium" }}>
                                                {stats.totalEvents || 0}
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Total Inviters
                                            </Typography>
                                            <Typography variant="h5" sx={{ fontWeight: "medium" }}>
                                                {stats.totalInviters || 0}
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Total Invites
                                            </Typography>
                                            <Typography variant="h5" sx={{ fontWeight: "medium" }}>
                                                {stats.totalInvites || 0}
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Total Reward Granted
                                            </Typography>
                                            <Typography variant="h5" sx={{ fontWeight: "medium" }}>
                                                {stats.totalRewardGranted || 0}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                    {stats.inviteStatusCounts && (
                                        <>
                                            <Divider sx={{ my: 2 }} />
                                            <Typography variant="subtitle1" gutterBottom>
                                                Overall Invite Status Counts
                                            </Typography>
                                            <Grid container spacing={2} sx={{ mt: 1 }}>
                                                <Grid size={{ xs: 6, sm: 3 }}>
                                                    <Typography variant="subtitle2" color="text.secondary">
                                                        Sent
                                                    </Typography>
                                                    <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                                                        {stats.inviteStatusCounts.sent || 0}
                                                    </Typography>
                                                </Grid>
                                                <Grid size={{ xs: 6, sm: 3 }}>
                                                    <Typography variant="subtitle2" color="text.secondary">
                                                        Active
                                                    </Typography>
                                                    <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                                                        {stats.inviteStatusCounts.joined || 0}
                                                    </Typography>
                                                </Grid>
                                                <Grid size={{ xs: 6, sm: 3 }}>
                                                    <Typography variant="subtitle2" color="text.secondary">
                                                        Verified
                                                    </Typography>
                                                    <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                                                        {stats.inviteStatusCounts.verified || 0}
                                                    </Typography>
                                                </Grid>
                                                <Grid size={{ xs: 6, sm: 3 }}>
                                                    <Typography variant="subtitle2" color="text.secondary">
                                                        Rejected
                                                    </Typography>
                                                    <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                                                        {stats.inviteStatusCounts.rejected || 0}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    )}
                </Grid>
            )}
        </Box>
    );
}

